import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, Button, Table, SearchField, ThreeDotPopover, useToast } from '@dreamtree-org/twreact-ui';
import { Plus, Search } from 'lucide-react';
import { PageHeader, QueryState, EmptyState, StatusBadge, Money } from '@/components';
import { useQuery } from '@/lib/useQuery';
import { formatDate, errorText } from '@/lib/format';
import Can from '@rbac/Can';
import { useCan } from '@rbac/useCan';
import { useCrudList, useCrudCount, useCrudWrite } from '../hooks';
import StatusTabs from '../components/StatusTabs';
import { modelName } from '../lib/catalog';
import { discountPct } from '../lib/money';
import { listingMenu } from '../lib/listing';
import ReadinessBadge from '../components/ReadinessBadge';

const TABS = [
  { key: 'all', label: 'All' },
  { key: 'draft', label: 'Draft' },
  { key: 'pending_review', label: 'Awaiting review' },
  { key: 'published', label: 'Published' },
  { key: 'unpublished', label: 'Unpublished' },
];

/**
 * SELL-04 — real listing list (`embedded`: the Products "Listed" tab). Click a row to edit; publishing is gated by catalog:publish.
 * MERCH-58: thumbnail, grade, inspection and readiness come from one Listing.overview call for the loaded rows.
 */
export default function Listings({ embedded = false }) {
  const nav = useNavigate();
  const can = useCan();
  const { toast } = useToast();
  const write = useCrudWrite();
  const [tab, setTab] = useState('all');
  const [term, setTerm] = useState('');
  const list = useCrudList('Listing', {
    where: tab === 'all' ? {} : { status: tab },
    with: ['InventoryUnit', 'InventoryUnit.ProductModel'],
    orderBy: { column: 'id', direction: 'desc' },
    limit: 200,
  });
  const counts = {
    all: useCrudCount('Listing', {}),
    draft: useCrudCount('Listing', { status: 'draft' }),
    pending_review: useCrudCount('Listing', { status: 'pending_review' }),
    published: useCrudCount('Listing', { status: 'published' }),
    unpublished: useCrudCount('Listing', { status: 'unpublished' }),
  };
  const ids = useMemo(() => (list.data || []).map((l) => l.id), [list.data]);
  const overview = useQuery('listingOverview', { ids }, { select: (r) => r, enabled: ids.length > 0 });
  const info = (overview.data && overview.data.data) || {};
  const canPublish = can('catalog', 'publish');
  const canUpdate = can('catalog', 'update');
  const rows = useMemo(() => {
    const t = term.trim().toLowerCase();
    return (list.data || []).filter((l) => !t || `${l.title} ${l.InventoryUnit ? l.InventoryUnit.internal_sku : ''}`.toLowerCase().includes(t));
  }, [list.data, term]);
  const reloadAll = () => {
    list.reload();
    overview.reload();
    Object.values(counts).forEach((c) => c.reload());
  };

  const setStatus = async (l, status) => {
    try {
      await write.update('Listing', l.id, { status });
      toast.success(status === 'published' ? 'Sent to publish' : 'Unpublished', l.title);
      reloadAll();
    } catch (ex) {
      toast.error('Could not change the listing', errorText(ex));
    }
  };
  const ACTIONS = {
    edit: (l) => nav(`/listings/${l.id}`),
    view: (l) => window.open(`/p/${l.id}`, '_blank', 'noopener'),
    publish: (l) => setStatus(l, 'published'),
    unpublish: (l) => setStatus(l, 'unpublished'),
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title={embedded ? undefined : 'Listings'}
        subtitle="What shoppers see on your storefront: one listing per physical unit."
        actions={<Can module="catalog" action="create"><Button variant={embedded ? 'outline' : 'primary'} leftIcon={<Plus size={16} />} onClick={() => nav('/listings/new')}>List a unit in stock</Button></Can>}
      />
      <StatusTabs value={tab} onChange={setTab} tabs={TABS.map((t) => ({ ...t, count: counts[t.key].data }))} />
      <div className="max-w-md">
        <SearchField
          placeholder="Search title or SKU…"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          leftIcon={<Search size={16} />}
          aria-label="Search listings" />
      </div>
      <QueryState query={list}>
        {() =>
          rows.length ? (
            <Table
              data={rows}
              columns={[
                {
                  key: 'cover', label: '', sortable: false,
                  render: (l) => <Avatar size="md" shape="rounded" src={(info[l.id] && info[l.id].cover_url) || undefined} name={l.title} alt="" />,
                },
                {
                  key: 'title', label: 'Listing',
                  render: (l) => (
                    <div className="min-w-0">
                      <div className="font-medium">{l.title}</div>
                      <div className="text-xs opacity-70">{l.InventoryUnit ? `${l.InventoryUnit.internal_sku} · ${modelName(l.InventoryUnit.ProductModel)}` : `Unit #${l.unit_id}`}</div>
                    </div>
                  ),
                },
                { key: 'grade', label: 'Grade', render: (l) => (l.InventoryUnit && l.InventoryUnit.grade ? `Grade ${l.InventoryUnit.grade}` : '—'), hideOnMobile: true },
                { key: 'price_paise', label: 'Price', render: (l) => <Money paise={l.price_paise} /> },
                { key: 'mrp_paise', label: 'MRP', render: (l) => (l.mrp_paise ? <span><Money paise={l.mrp_paise} /> <span className="text-xs opacity-70">({discountPct(l.price_paise, l.mrp_paise)}% off)</span></span> : '—'), hideOnMobile: true },
                {
                  key: 'inspection', label: 'Inspection', hideOnMobile: true,
                  render: (l) => {
                    const i = info[l.id] && info[l.id].inspection;
                    return i ? <span className="tabular-nums">{i.passed}/{i.total} passed</span> : <span className="opacity-60">—</span>;
                  },
                },
                { key: 'readiness', label: 'Readiness', render: (l) => (['published', 'pending_review'].includes(l.status) ? <span className="opacity-60">—</span> : <ReadinessBadge result={info[l.id]} />) },
                { key: 'status', label: 'Status', render: (l) => <StatusBadge status={l.status} /> },
                { key: 'published_at', label: 'Published', render: (l) => formatDate(l.published_at), hideOnMobile: true },
                {
                  key: 'menu', label: '', sortable: false,
                  render: (l) => (
                    // The menu lives in the row: keep its clicks (and keys) from opening the row.
                    <span role="presentation" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                      <ThreeDotPopover
                        ariaLabel={`Actions for ${l.title}`}
                        items={listingMenu(l, { canUpdate, canPublish, ready: Boolean(info[l.id] && info[l.id].ready) }).map((it) => ({ ...it, onClick: () => ACTIONS[it.key](l) }))}
                      />
                    </span>
                  ),
                },
              ]}
              onRowClick={(l) => nav(`/listings/${l.id}`)}
              showSerial={false}
              withAction={false}
              showColumnPicker={false}
              pagination
              pageSize={25}
              onReload={reloadAll}
            />
          ) : (
            <EmptyState title={term || tab !== 'all' ? 'No listings match' : 'No listings yet'} action={<Can module="catalog" action="create"><Button onClick={() => nav('/add')}>Add listing</Button></Can>}>
              List an in-stock unit to put it on your storefront.
            </EmptyState>
          )
        }
      </QueryState>
    </div>
  );
}
